import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import {
  Box,
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  FormHelperText,
  Input,
  Textarea,
  Select,
  VStack,
  HStack,
  Text,
  Grid,
  Spinner,
  Checkbox,
} from '@chakra-ui/react'
import { apiClient } from '@/lib/api/client'
import { useToastNotification } from '@/lib/utils/useToastNotification'
import {
  createPageSchema,
  updatePageSchema,
  type CreatePageFormData,
  type UpdatePageFormData,
} from '@/lib/validations/auth'
import { PageBuilder } from '@/components/pageBuilder/PageBuilder'
import type { PageLayoutStructure } from '@/types/pageBuilder'

interface PageFormProps {
  pageId?: number
  onSuccess?: () => void
  onCancel?: () => void
}

const statusOptions = [
  { value: 'draft', label: 'Draft' },
  { value: 'published', label: 'Published' },
  { value: 'scheduled', label: 'Scheduled' },
]

type PageFormValues = CreatePageFormData | UpdatePageFormData

export function PageForm({ pageId, onSuccess, onCancel }: PageFormProps) {
  const navigate = useNavigate()
  const { showError, showSuccess } = useToastNotification()

  const [isLoading, setIsLoading] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [layoutStructure, setLayoutStructure] = useState<PageLayoutStructure | null>(null)
  const isEditMode = !!pageId

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    reset,
  } = useForm<PageFormValues>({
    resolver: zodResolver(isEditMode ? updatePageSchema : createPageSchema),
    defaultValues: {
      status: 'draft',
      layout: 'default',
      active: true,
      content: '',
      layout_structure: null,
      meta_title: '',
      meta_description: '',
      published_at: '',
    },
  })

  const titleValue = watch('title')
  const statusValue = watch('status')
  const activeValue = watch('active')

  useEffect(() => {
    if (!isEditMode && titleValue) {
      const slug = titleValue
        .toLowerCase()
        .trim()
        .replace(/[^a-z0-9\s-]/g, '')
        .replace(/\s+/g, '-')
        .replace(/-+/g, '-')
      setValue('slug', slug, { shouldValidate: false })
    }
  }, [titleValue, isEditMode, setValue])

  useEffect(() => {
    if (isEditMode && pageId) {
      setIsLoading(true)
      apiClient
        .get(`/content-management/pages/${pageId}`)
        .then(({ data }) => {
          const pageData = data?.data || data || {}

          reset({
            title: pageData.title || '',
            slug: pageData.slug || '',
            content: pageData.content || '',
            layout_structure: pageData.layout_structure || null,
            meta_title: pageData.meta_title || '',
            meta_description: pageData.meta_description || '',
            status: pageData.status || 'draft',
            published_at: pageData.published_at ? new Date(pageData.published_at).toISOString().slice(0, 16) : '',
            active:
              pageData.active !== undefined
                ? Boolean(pageData.active === 'Active' || pageData.active === true)
                : true,
          })

          setLayoutStructure(pageData.layout_structure || null)
        })
        .catch((error: any) => {
          showError(error)
        })
        .finally(() => setIsLoading(false))
    }
  }, [isEditMode, pageId, reset, showError])

  const handleSubmitForm = async (data: PageFormValues) => {
    setIsSubmitting(true)
    try {
      const payload: Record<string, any> = {
        title: data.title,
        slug: data.slug,
        content: data.content || null,
        layout_structure: layoutStructure,
        layout: 'default',
        featured_image: null,
        meta_title: data.meta_title || null,
        meta_description: data.meta_description || null,
        status: data.status,
        active: data.active,
      }

      if (data.status === 'scheduled' && data.published_at) {
        payload.published_at = new Date(data.published_at).toISOString()
      } else if (data.status === 'published') {
        payload.published_at = new Date().toISOString()
      } else {
        payload.published_at = null
      }

      if (isEditMode) {
        await apiClient.put(`/content-management/pages/${pageId}`, payload)
        showSuccess('Success', 'Page updated successfully')
      } else {
        await apiClient.post('/content-management/pages', payload)
        showSuccess('Success', 'Page created successfully')
      }

      if (onSuccess) {
        onSuccess()
      } else {
        navigate('/content-management/pages')
      }
    } catch (error: any) {
      showError(error)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleCancel = () => {
    if (onCancel) {
      onCancel()
    } else {
      navigate('/content-management/pages')
    }
  }

  if (isLoading) {
    return (
      <VStack spacing={4} py={8}>
        <Spinner size="xl" color="blue.500" />
        <Text>Loading page...</Text>
      </VStack>
    )
  }

  return (
    <Box as="form" onSubmit={handleSubmit(handleSubmitForm)}>
      <VStack spacing={4} align="stretch">
        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">
            Title <Text as="span" color="red.500">*</Text>
          </FormLabel>
          <FormControl isInvalid={!!errors.title}>
            <Input {...register('title')} placeholder="e.g. About Us, Contact, Home" />
            <FormErrorMessage>{errors.title?.message}</FormErrorMessage>
            <FormHelperText>The page title.</FormHelperText>
          </FormControl>
        </Grid>

        <Box>
          <FormLabel mb={3} fontWeight="medium">
            Page Content
          </FormLabel>
          <Box h="700px">
            <PageBuilder
              value={layoutStructure}
              onChange={(structure) => {
                setLayoutStructure(structure)
                setValue('layout_structure', structure, { shouldValidate: false })
              }}
            />
          </Box>
          <FormHelperText mt={2}>
            Build your page using drag-and-drop blocks. Click blocks from the right sidebar to add
            them, then click on blocks to edit their settings.
          </FormHelperText>
        </Box>

        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">
            Slug <Text as="span" color="red.500">*</Text>
          </FormLabel>
          <FormControl isInvalid={!!errors.slug}>
            <Input {...register('slug')} placeholder="e.g. about-us, contact, home" />
            <FormErrorMessage>{errors.slug?.message}</FormErrorMessage>
            <FormHelperText>The slug is used in URLs. Auto-generated from title, but customizable.</FormHelperText>
          </FormControl>
        </Grid>

        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">
            Status
          </FormLabel>
          <FormControl isInvalid={!!errors.status}>
            <Select {...register('status')}>
              {statusOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Select>
            <FormErrorMessage>{errors.status?.message}</FormErrorMessage>
            <FormHelperText>Set the publication status of the page.</FormHelperText>
          </FormControl>
        </Grid>

        {statusValue === 'scheduled' && (
          <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
            <FormLabel pt={2} fontWeight="medium">
              Publish Date & Time
            </FormLabel>
            <FormControl isInvalid={!!errors.published_at}>
              <Input type="datetime-local" {...register('published_at')} />
              <FormErrorMessage>{errors.published_at?.message}</FormErrorMessage>
              <FormHelperText>Schedule when this page should be published.</FormHelperText>
            </FormControl>
          </Grid>
        )}

        <Box pt={4} borderTop="1px" borderColor="gray.200">
          <Text fontSize="md" fontWeight="semibold" mb={4}>
            SEO Settings
          </Text>

          <VStack spacing={4} align="stretch">
            <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
              <FormLabel pt={2} fontWeight="medium">
                Meta Title
              </FormLabel>
              <FormControl isInvalid={!!errors.meta_title}>
                <Input {...register('meta_title')} placeholder="SEO title for search engines" />
                <FormErrorMessage>{errors.meta_title?.message}</FormErrorMessage>
                <FormHelperText>SEO meta title (recommended: 50-60 characters).</FormHelperText>
              </FormControl>
            </Grid>

            <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
              <FormLabel pt={2} fontWeight="medium">
                Meta Description
              </FormLabel>
              <FormControl isInvalid={!!errors.meta_description}>
                <Textarea
                  {...register('meta_description')}
                  placeholder="SEO description for search engines"
                  rows={3}
                />
                <FormErrorMessage>{errors.meta_description?.message}</FormErrorMessage>
                <FormHelperText>SEO meta description (recommended: 150-160 characters).</FormHelperText>
              </FormControl>
            </Grid>
          </VStack>
        </Box>

        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">
            Active
          </FormLabel>
          <FormControl>
            <Checkbox isChecked={activeValue} onChange={(e) => setValue('active', e.target.checked)}>
              Active
            </Checkbox>
            <FormHelperText>Whether this page is active and visible.</FormHelperText>
          </FormControl>
        </Grid>

        <HStack spacing={4} justify="flex-end" pt={4}>
          <Button variant="outline" onClick={handleCancel} isDisabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            type="submit"
            colorScheme="blue"
            isLoading={isSubmitting}
            loadingText={isEditMode ? 'Saving...' : 'Creating...'}
          >
            {isEditMode ? 'Save' : 'Create Page'}
            {isSubmitting && (
              <Text as="span" ml={2} fontSize="sm">
                ...
              </Text>
            )}
          </Button>
        </HStack>
      </VStack>
    </Box>
  )
}


